import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import LabeledControl from "@editor/components/common/designSystem/LabeledControl";
import Selectable from "@editor/components/common/designSystem/Selectable";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import useInstallShopify from "@editor/hooks/useInstallShopify";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { ArrowUpRight } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

type CreateShopFormData = {
  name: string;
  workspaceId: string;
  shopifyIntegrationId?: string | null;
};

type CreateShopFlowProps = {
  workspaceId?: string;
};
const CreateShopFlow: React.FC<CreateShopFlowProps> = ({ workspaceId }) => {
  const currentWorkspaceId = useCurrentWorkspaceId();
  return (
    <CreateShopForm
      initialWorkspaceId={workspaceId ?? currentWorkspaceId ?? undefined}
    />
  );
};

function CreateShopForm({
  initialWorkspaceId,
}: {
  initialWorkspaceId: string | undefined;
}) {
  const { closeModal } = useModal();
  const location = useLocation();
  const { from } = location.state || {};
  const { user } = useCurrentUser();
  const { register, handleSubmit, control, formState } =
    useForm<CreateShopFormData>({
      defaultValues: {
        name: "",
        workspaceId: initialWorkspaceId,
        shopifyIntegrationId: null,
      },
      mode: "onBlur",
    });

  const installShopify = useInstallShopify();
  const analytics = useLogAnalytics();
  const { mutateAsync: createShop, isPending: isCreatingShop } =
    trpc.project.create.useMutation({
      onError: (error) => {
        if (error.data.key === "project.integration.alreadyAssigned") {
          closeModal({ type: "createShopFlowModal" });
        }
      },
    });
  const navigate = useNavigate();

  async function onSubmit({
    name,
    workspaceId,
    shouldConnectShopifyStore,
  }: CreateShopFormData & { shouldConnectShopifyStore: boolean }) {
    const { project: newProject } = await createShop({
      name: name ?? `${user?.name}'s project`,
      // TODO (Ben, 2024-10-09): Figure out what to do
      // with "NEW_WORKSPACE_ID": we were adding null for the
      // workspaceId if it was equal to "NEW_WORKSPACE_ID"
      workspaceId,
      shopifyIntegrationId: null,
    });
    analytics("project.create", {
      withShopify: Boolean(shouldConnectShopifyStore),
      createdFrom: from === "allProjects" ? "allProjects" : "orgPage",
    });
    void trpcUtils.workspace.getUserWorkspacesList.invalidate();
    void trpcUtils.project.findByUserId.invalidate();
    void trpcUtils.project.listWithStats.invalidate();
    void trpcUtils.project.listGroupedByWorkspaceForUser.invalidate();
    if (!newProject) {
      return;
    }
    if (shouldConnectShopifyStore) {
      await installShopify({
        cookie: {
          type: "newProject",
          projectId: newProject.id,
          workspaceId: newProject.ownerWorkspaceId,
        },
        workspaceId: newProject.ownerWorkspaceId,
      });
    } else {
      // Patrick (2025-06-03): This is a placeholder navigation until we figure out where to go after creating a shop
      navigate(`/shops/${newProject.id}/site`);
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeModal({ type: "createShopFlowModal" });
    }
  };

  return (
    <form
      onSubmit={(data) => {
        void handleSubmit((data) => {
          void onSubmit({
            ...data,
            shouldConnectShopifyStore: true,
          });
        })(data);
      }}
    >
      <Modal
        isOpen={true}
        onOpenChange={handleOpenChange}
        size="base"
        title="New Shop"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="base"
              onClick={() => {
                void handleSubmit((data) => {
                  void onSubmit({
                    ...data,
                    shouldConnectShopifyStore: false,
                  });
                })();
              }}
            >
              Start Building First
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="base"
              disabled={!formState.isValid || isCreatingShop}
              isLoading={isCreatingShop}
              endEnhancer={<ArrowUpRight size={16} />}
            >
              Add Integration
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-default font-medium">
              Connect your Shopify Store
            </h2>
            <p className="text-sm text-muted">
              This allows Replo to automatically pull in your products, theme,
              and other useful information.
            </p>
            <img
              src="/images/flows/integrate-shopify-full.png"
              alt="Integrate with Shopify"
              className="my-1 w-full rounded-lg"
            />
          </div>
          <LabeledControl label="Shop Name">
            <Input
              autoFocus
              placeholder="Shop Name"
              {...register("name", {
                required: "Please enter a shop name.",
              })}
              autoComplete="off"
              size="base"
            />
          </LabeledControl>
          <LabeledControl label="Workspace">
            <Controller
              render={({ field: { onChange, value } }) => (
                <WorkspaceSelectable value={value} onChange={onChange} />
              )}
              control={control}
              name="workspaceId"
            />
          </LabeledControl>
        </div>
      </Modal>
    </form>
  );
}

function WorkspaceSelectable({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (value: string) => void;
}) {
  const { data } = trpc.workspace.getUserWorkspacesList.useQuery();
  const workspaces = data?.workspaces ?? [];
  return (
    <Selectable
      size="base"
      areOptionsSearchable={workspaces.length > 5}
      placeholder="Choose Workspace"
      options={workspaces.map((workspace) => ({
        label: workspace.name,
        value: workspace.id,
      }))}
      value={value}
      onSelect={onChange}
    />
  );
}

export default CreateShopFlow;
